import type { LangChainMessage } from "@assistant-ui/react-langgraph";

import type { AgentUploadedFile } from "@/agents-v2/api";
import {
  AGENT_UPLOAD_DATA_PART_NAME,
  AGENT_UPLOADED_FILES_METADATA_KEY
} from "@/agents-v2/uploads/constants";
import {
  getRegisteredAgentUploadFile,
  unregisterAgentUploadFile
} from "@/agents-v2/uploads/file-registry";
import { openExternal } from "@/agents-v2/utils/open-external";
import { asRecord, stringValue } from "@/agents-v2/utils/unknown";

export type { AgentUploadedFile } from "@/agents-v2/api";
export { AGENT_UPLOADED_FILES_METADATA_KEY } from "@/agents-v2/uploads/constants";

const parseDataUrl = (
  value: string
): { mimeType: string; base64: string } | null => {
  const match = /^data:([^;,]+)?;base64,(.*)$/s.exec(value);
  if (!match) return null;
  return {
    mimeType: match[1] || "application/octet-stream",
    base64: match[2] ?? ""
  };
};

const base64ToFile = (
  base64: string,
  filename: string,
  mimeType: string
): File => {
  const cleanBase64 = base64.includes(",")
    ? (base64.split(",").pop() ?? "")
    : base64;
  const binary = window.atob(cleanBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new File([bytes], filename || "upload", {
    type: mimeType || "application/octet-stream"
  });
};

type UploadFilePart = {
  file: File;
  marker?: string;
};

const uploadFileFromMessagePart = (part: unknown): UploadFilePart | null => {
  const record = asRecord(part);
  const type = stringValue(record.type);

  if (
    type === "data" &&
    stringValue(record.name) === AGENT_UPLOAD_DATA_PART_NAME
  ) {
    const file = asRecord(record.data).file;
    return file instanceof File ? { file } : null;
  }

  const directFile = record.file;
  if (directFile instanceof File) return { file: directFile };

  if (type === "image") {
    const image = stringValue(record.image);
    const parsed = image ? parseDataUrl(image) : null;
    if (!parsed) return null;
    return {
      file: base64ToFile(
        parsed.base64,
        stringValue(record.filename) ?? "image",
        parsed.mimeType
      )
    };
  }

  if (type === "image_url") {
    const imageUrl = record.image_url;
    const url =
      typeof imageUrl === "string"
        ? imageUrl
        : stringValue(asRecord(imageUrl).url);
    const parsed = url ? parseDataUrl(url) : null;
    if (!parsed) return null;
    return {
      file: base64ToFile(
        parsed.base64,
        stringValue(record.filename) ?? "image",
        parsed.mimeType
      )
    };
  }

  if (type !== "file") return null;

  const marker = stringValue(record.data);
  const registeredFile = getRegisteredAgentUploadFile(marker);
  if (registeredFile) {
    return { file: registeredFile, marker: marker ?? undefined };
  }

  const nestedFile = asRecord(record.file);
  const metadata = asRecord(record.metadata);
  const filename =
    stringValue(record.filename) ??
    stringValue(metadata.filename) ??
    stringValue(nestedFile.filename) ??
    "upload";
  const mimeType =
    stringValue(record.mimeType) ??
    stringValue(record.mime_type) ??
    stringValue(nestedFile.mime_type) ??
    "application/octet-stream";
  const base64 =
    stringValue(record.data) ??
    stringValue(record.base64) ??
    stringValue(nestedFile.file_data);

  return base64 ? { file: base64ToFile(base64, filename, mimeType) } : null;
};

export type UploadAgentFilesForThread = (
  files: readonly File[],
  signal?: AbortSignal
) => Promise<AgentUploadedFile[]>;

const withUploadedFileMetadata = (
  message: Extract<LangChainMessage, { type: "human" }>,
  uploadedFiles: readonly AgentUploadedFile[],
  retainedContent: unknown[]
): LangChainMessage => {
  const additionalKwargs = asRecord(message.additional_kwargs);
  const metadata = asRecord(additionalKwargs.metadata);
  const existingFiles = metadata[AGENT_UPLOADED_FILES_METADATA_KEY];
  const nextUploadedFiles = [
    ...(Array.isArray(existingFiles) ? existingFiles : []),
    ...uploadedFiles
  ];

  return {
    ...message,
    content: retainedContent as Exclude<
      Extract<LangChainMessage, { type: "human" }>["content"],
      string
    >,
    additional_kwargs: {
      ...additionalKwargs,
      metadata: {
        ...metadata,
        [AGENT_UPLOADED_FILES_METADATA_KEY]: nextUploadedFiles
      }
    }
  };
};

export const prepareUploadedFileMessages = async (
  messages: readonly LangChainMessage[],
  uploadAgentFiles: UploadAgentFilesForThread,
  signal?: AbortSignal
): Promise<LangChainMessage[]> => {
  const prepared: LangChainMessage[] = [];

  for (const message of messages) {
    if (message.type !== "human" || !Array.isArray(message.content)) {
      prepared.push(message);
      continue;
    }

    const uploadParts: UploadFilePart[] = [];
    const retainedContent: unknown[] = [];
    for (const part of message.content) {
      const uploadPart = uploadFileFromMessagePart(part);
      if (uploadPart) {
        uploadParts.push(uploadPart);
      } else {
        retainedContent.push(part);
      }
    }

    if (uploadParts.length === 0) {
      prepared.push(message);
      continue;
    }

    try {
      const uploadedFiles = await uploadAgentFiles(
        uploadParts.map((part) => part.file),
        signal
      );
      if (uploadedFiles.length !== uploadParts.length) {
        throw new Error(
          `Expected ${uploadParts.length} uploaded file records, received ${uploadedFiles.length}.`
        );
      }
      prepared.push(
        withUploadedFileMetadata(message, uploadedFiles, retainedContent)
      );
    } finally {
      for (const part of uploadParts) {
        unregisterAgentUploadFile(part.marker);
      }
    }
  }

  return prepared;
};

const uploadedFileFromRecord = (record: unknown): AgentUploadedFile | null => {
  const source = asRecord(record);
  const filename = stringValue(source.filename);
  const displayName = stringValue(source.display_name) ?? filename;
  const path = stringValue(source.path);
  const mimeType = stringValue(source.mime_type) ?? "application/octet-stream";
  const size = source.size;

  if (!filename || !displayName || !path) return null;

  return {
    filename,
    display_name: displayName,
    path,
    mime_type: mimeType,
    size: typeof size === "number" && size >= 0 ? size : 0
  };
};

export const getUploadedFilesFromMetadata = (
  metadata: unknown
): AgentUploadedFile[] => {
  const files = asRecord(metadata)[AGENT_UPLOADED_FILES_METADATA_KEY];
  return Array.isArray(files)
    ? files.flatMap((file) => {
        const uploadedFile = uploadedFileFromRecord(file);
        return uploadedFile ? [uploadedFile] : [];
      })
    : [];
};

export const openDownloadUrl = (url: string, filename: string): void => {
  // Inside Office the pane's webview may refuse window.open, so the
  // Office-aware opener hands the URL to the system browser (pane-specific).
  void filename;
  openExternal(url);
};
